import { addMonths } from "date-fns"

import { formatDate, getTodayCalendarDate, toCalendarDate } from "@/utils/dateUtil"

import { Base } from "./Base"
import { Epic } from "./Epic"
import { Filter } from "./Pageable/Filter"
import { ProjectStats } from "./Project"
import { ServerResponse } from "./ServerResponse"

export type MilestoneStatus = "COMPLETED" | "ACTIVE" | "COMING" | "AT_RISK" | "REVIEW" | "STOPPED"

export const getMilestoneTitle = (title: string) => title.trim().replace(/^milestone\s+/i, "")

export const renderMilestoneName = (title: string) => {
  const normalizedTitle = getMilestoneTitle(title)
  return normalizedTitle ? `Milestone ${normalizedTitle}` : ""
}

export const getNextMilestoneTitle = (title: string) => {
  const normalizedTitle = getMilestoneTitle(title).toUpperCase()

  if (!normalizedTitle) {
    return "A"
  }

  const letters = normalizedTitle.split("")
  let index = letters.length - 1

  while (index >= 0 && letters[index] === "Z") {
    letters[index] = "A"
    index--
  }

  if (index < 0) {
    letters.unshift("A")
  } else {
    letters[index] = String.fromCharCode(letters[index].charCodeAt(0) + 1)
  }

  return letters.join("")
}
export interface MilestoneAddEpic {
  milestoneId: string
  epicId: string
  item?: Epic
}

export interface MilestonePreviewResponse {
  milestones: ServerResponse<MilestonePreview>
  allMilestonesAllTimeMetrics: ProjectStats
}

export interface MilestonePreview {
  id: string
  title: string
  startDate: string
  endDate: string
  chartInfo: any
  aiChartInfo?: any
}

export class Milestone extends Base<Milestone> {
  id?: string
  title = "A"
  _title = ""
  endDate: Date = getTodayCalendarDate()
  startDate: Date = getTodayCalendarDate()
  epics: Epic[] = []
  newMilestone?: boolean
  notEstimatedItemCount?: number
  totalStoryPoints?: number
  status: MilestoneStatus = "COMING"
  goalId?: string

  constructor(props?: Partial<Milestone>) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      if (props[field] === undefined) return
      this[field] = props[field]
    })

    this.endDate = toCalendarDate(this.endDate)
    this.startDate = toCalendarDate(this.startDate)
    this.epics = this.epics.map(epic => Epic.create(epic))
    this.title = getMilestoneTitle(this.title)
  }

  getJsonObj(): { [key in keyof Milestone]? } {
    return {
      title: this.title,
      endDate: formatDate(this.endDate, "yyyy-MM-dd", { dateOnly: true }),
      startDate: formatDate(this.startDate, "yyyy-MM-dd", { dateOnly: true }),
      epics: this.epics.map(e => e.id),
      goalId: this.goalId
    }
  }
  static createDefault(): Milestone {
    return new Milestone({
      title: "A",
      endDate: addMonths(getTodayCalendarDate(), 1),
      startDate: getTodayCalendarDate(),
      epics: [],
      newMilestone: true,
      status: "COMING"
    })
  }

  get titleParsed() {
    return renderMilestoneName(this.title)
  }

  get isReview() {
    return this.status === "REVIEW"
  }

  get isCompleted() {
    return this.status === "COMPLETED" || this.isReview
  }

  get isActive() {
    return this.status === "ACTIVE"
  }

  get isComing() {
    return this.status === "COMING"
  }

  get isStopped() {
    return this.status === "STOPPED"
  }

  get isAtRisk() {
    return this.status === "AT_RISK"
  }

  get simpleDiff() {
    if (this.id) return ""
    return {
      endDate: this.endDate,
      startDate: this.startDate,
      epics: this.epics
    }
  }

  static sortMilestonesByTitle(milestones: Milestone[]) {
    return milestones.sort(
      (a, b) => a.title.length - b.title.length || a.title.localeCompare(b.title)
    )
  }
}

export class MilestoneSimplify {
  endDate = ""
  startDate = ""
  title = ""
  projectId = ""
  id = ""
  status: MilestoneStatus = "COMING"

  constructor(props?: Partial<MilestoneSimplify>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.title = getMilestoneTitle(this.title)
  }

  get titleParsed() {
    return renderMilestoneName(this.title)
  }
}

export class ChangeMilestoneDate {
  milestone: Milestone
  startDate: Date = getTodayCalendarDate()
  endDate: Date = getTodayCalendarDate()
  milestoneId = ""
  constructor(props?: Partial<ChangeMilestoneDate>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    const data = {
      milestoneId: this.milestone?.id ?? "",
      endDate: formatDate(this.endDate, "yyyy-MM-dd", { dateOnly: true }),
      startDate: formatDate(this.startDate, "yyyy-MM-dd", { dateOnly: true })
    }
    return data
  }
}

export class MilestoneSearch extends Filter {
  q = ""
  projectName = ""
  projectIds: string[] = []

  constructor(props?: Partial<MilestoneSearch>) {
    super(props as Filter)
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    const data = {
      q: this.q,
      projectIds: this.projectIds
    }
    return super.getJsonObj({ params: data })
  }
}
